/** API DTOs shared by the server and the web client. Money is base/native minor units; rates are ×10 000. */
import type { MoneyState, Target } from './budget';
import type { IsoDate, Month } from './dates';
import type { InterestMethod, LoanRate } from './loan';
import type { Currency, NumberLocale } from './money';
import type { Frequency } from './schedule';

export type AccountKind = 'checking' | 'savings' | 'cash' | 'credit' | 'asset' | 'liability' | 'loan';
/** Where an account counts: in the budget, as a tracked asset, or as a debt (loans.md §5). */
export type AccountGroup = 'budget' | 'tracking' | 'debt';
export const DEBT_KINDS: AccountKind[] = ['loan', 'liability'];
export const accountGroup = (a: { onBudget: boolean; kind: AccountKind }): AccountGroup =>
  a.onBudget ? 'budget' : DEBT_KINDS.includes(a.kind) ? 'debt' : 'tracking';
export type Cleared = 'uncleared' | 'cleared' | 'reconciled';
export const FLAGS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'] as const;
export type Flag = (typeof FLAGS)[number];
/** Category reference on a transaction: a category id, 'rta' (Inflow: Ready to Assign) or null. */
export type CategoryRef = string | 'rta' | null;
export const MEMBER_COLORS = ['#2f6f9f', '#b0587c', '#5f7d3a', '#9c6b2f', '#6a5acd'] as const;

export interface MemberDto {
  id: string;
  displayName: string;
  email: string;
  role: 'owner' | 'member';
  color: string;
  initial: string;
  joined: boolean;
  lastActiveAt: string | null;
}

export interface HouseholdDto {
  id: string;
  name: string;
  baseCurrency: Currency;
  numberLocale: NumberLocale;
  weekStart: 'mon' | 'sun';
  baseCurrencyLocked: boolean;
  members: MemberDto[];
}

export interface MeDto {
  user: { id: string; email: string; name: string; picture: string | null };
  member: MemberDto | null;
  household: HouseholdDto | null;
  today: IsoDate;
}

export interface AuthConfigDto {
  google: boolean;
  devLogin: boolean;
}

export interface RateDto {
  date: IsoDate;
  rate: number;
  source: 'nbs' | 'manual' | 'fallback';
}

export interface AccountDto {
  id: string;
  name: string;
  institution: string;
  currency: Currency;
  kind: AccountKind;
  group: AccountGroup;
  onBudget: boolean;
  closed: boolean;
  sortOrder: number;
  cleared: number;
  uncleared: number;
  working: number;
  /** Working balance in base currency at today's rate. */
  workingBase: number;
  clearedBase: number;
  unclearedBase: number;
  unapprovedCount: number;
  cards: { last4: string; label: string }[];
  lastReconciledAt: string | null;
}

export interface AutoAssignValues {
  underfunded: number;
  lastAssigned: number;
  lastSpent: number;
  avgSpent: number;
  avgAssigned: number;
}

export interface PlanCategoryDto {
  id: string;
  groupId: string;
  name: string;
  note: string;
  pinned: boolean;
  hidden: boolean;
  target: Target | null;
  carryIn: number;
  assigned: number;
  activity: number;
  available: number;
  monthlyNeed: number;
  funded: number;
  need: number;
  state: MoneyState;
  auto: AutoAssignValues;
}

export interface PlanGroupDto {
  id: string;
  name: string;
  hidden: boolean;
  categories: PlanCategoryDto[];
}

export interface PlanDto {
  month: Month;
  rta: number;
  fxThisMonth: number;
  overspentLastMonth: number;
  incomeThisMonth: number;
  ageOfMoney: number | null;
  underfunded: number;
  groups: PlanGroupDto[];
  uncategorized: { activity: number; available: number };
  summary: { carryIn: number; assigned: number; activity: number; available: number };
  costToBeMe: number;
  lastMonthIncome: number;
  rate: RateDto;
}

export interface SplitDto {
  id: string;
  categoryId: CategoryRef;
  categoryName: string;
  amount: number;
  baseAmount: number;
  memo: string;
}

export interface TransactionDto {
  id: string;
  accountId: string;
  accountName: string;
  currency: Currency;
  date: IsoDate;
  payeeId: string | null;
  payeeName: string;
  memo: string;
  amount: number;
  baseAmount: number;
  rate: number;
  rateSource: 'nbs' | 'manual' | 'transfer' | 'identity';
  categoryId: CategoryRef;
  categoryName: string;
  cleared: Cleared;
  approved: boolean;
  flag: Flag | null;
  splits: SplitDto[];
  transfer: {
    id: string;
    otherAccountId: string;
    otherAccountName: string;
    otherAmount: number;
    otherCurrency: Currency;
    impliedRate: number;
  } | null;
  importId: string | null;
  captureId: string | null;
  scheduledId: string | null;
  createdBy: string;
  createdAt: string;
}

export interface SplitInput {
  categoryId: CategoryRef;
  amount: number;
  memo?: string;
}

export interface TransactionInput {
  accountId: string;
  date: IsoDate;
  payeeName?: string;
  categoryId?: CategoryRef;
  amount: number;
  memo?: string;
  flag?: Flag | null;
  cleared?: Cleared;
  /** Manual native→base rate override (×10 000). null resets to the NBS rate. */
  rate?: number | null;
  splits?: SplitInput[];
  /** Idempotency key for offline replays. */
  clientId?: string;
  approved?: boolean;
}

export interface TransferInput {
  fromAccountId: string;
  toAccountId: string;
  /** Positive amount leaving the From account (native). */
  fromAmount: number;
  /** Positive amount arriving in the To account (native). Ignored when currencies match. */
  toAmount?: number;
  date: IsoDate;
  memo?: string;
  categoryId?: CategoryRef;
  clientId?: string;
}

export interface PayeeDto {
  id: string;
  name: string;
  lastCategoryId: string | null;
  lastCategoryName: string | null;
  lastUsedAt: IsoDate | null;
}

export interface CardMappingDto {
  id: string;
  last4: string;
  label: string;
  accountId: string;
}

export interface UnmappedCardDto {
  last4: string;
  firstSeen: string;
  count: number;
}

export interface PayeeRuleDto {
  id: string;
  pattern: string;
  payeeId: string;
  payeeName: string;
  categoryId: string | null;
  categoryName: string | null;
  priority: number;
}

export type NeedsDecision = 'unknown_card' | 'duplicate' | 'currency_mismatch' | null;

export interface InboxItemDto {
  id: string;
  transactionId: string | null;
  merchantRaw: string;
  payeeName: string;
  amount: number;
  currency: Currency;
  baseAmount: number;
  last4: string | null;
  accountId: string | null;
  categoryId: string | null;
  confidence: number;
  suggestionSource: 'rule' | 'history' | 'fuzzy' | 'none' | null;
  rule: { pattern: string; payeeName: string } | null;
  memberId: string;
  deviceName: string | null;
  postedAt: string;
  needsDecision: NeedsDecision;
  duplicate: {
    transactionId: string;
    date: IsoDate;
    payeeName: string;
    amount: number;
    source: string;
  } | null;
  isTest: boolean;
  status: 'pending' | 'approved' | 'dismissed' | 'merged';
}

export interface InboxDto {
  items: InboxItemDto[];
  readyCount: number;
  lastCapture: { memberId: string; deviceName: string | null; at: string } | null;
}

export interface ApproveInput {
  categoryId?: CategoryRef;
  payeeName?: string;
  memo?: string;
  accountId?: string;
  flag?: Flag | null;
  splits?: SplitInput[];
  /** Create a payee rule so this merchant always maps to the chosen category. */
  rememberRule?: boolean;
}

export interface DeviceDto {
  id: string;
  name: string;
  kind: 'companion' | 'webhook';
  memberId: string;
  lastSeenAt: string | null;
  captureCount: number;
  createdAt: string;
}

export interface PairingDto {
  code: string;
  expiresAt: string;
  pairUrl: string;
  captureUrl: string;
}

export interface ActivityDto {
  id: string;
  memberId: string;
  kind: string;
  payload: Record<string, unknown>;
  at: string;
}

export interface HomeDto {
  rta: number;
  fxThisMonth: number;
  month: Month;
  overspent: { id: string; name: string; available: number }[];
  underfunded: { count: number; amount: number };
  inbox: {
    count: number;
    latest: { payeeName: string; amount: number; currency: Currency; at: string } | null;
  };
  pinned: { id: string; name: string; available: number; total: number; state: MoneyState }[];
  ageOfMoney: { days: number | null; history: (number | null)[]; deltaVsLastMonth: number | null };
  activity: ActivityDto[];
  accountsByCurrency: { currency: Currency; native: number; base: number }[];
  budgetTotalBase: number;
  /** Net worth today: budget money + other assets + debts (loans.md §5). */
  netWorth: { total: number; debts: number; loans: { accountId: string; name: string; base: number }[] };
  partnerLastActive: { memberId: string; at: string } | null;
}

export interface ReflectQuery {
  from: Month;
  to: Month;
  accountIds?: string[];
  categoryIds?: string[];
  memberId?: string;
}

export interface ReflectSlice {
  key: string;
  name: string;
  amount: number;
  /** Transactions (split parts count once each). */
  count: number;
}

export interface ReflectDto {
  from: Month;
  to: Month;
  months: Month[];
  spendingByCategory: { id: string; name: string; groupName: string; amount: number }[];
  /** `key` is the payee id, `__xfer:<accountId>` for a categorised transfer, or `__none`. */
  spendingByPayee: { id: string | null; key: string; name: string; amount: number }[];
  /** Category id → its payees (drill-down), sorted by amount. Split parts count under their own category. */
  payeesByCategory: Record<string, ReflectSlice[]>;
  /** Payee key → the categories its spending went to. */
  categoriesByPayee: Record<string, ReflectSlice[]>;
  trend: {
    groups: { id: string; name: string }[];
    rows: { month: Month; byGroup: Record<string, number> }[];
  };
  incomeVsExpense: {
    income: { name: string; byMonth: Record<Month, number> }[];
    expense: Record<Month, number>;
  };
  netWorth: NetWorthRow[];
  ageOfMoney: { month: Month; days: number | null }[];
}

export interface ImportRowInput {
  date: IsoDate;
  amount: number;
  payee: string;
  memo?: string;
  ref?: string;
}

export interface ImportResultDto {
  batchId: string;
  created: number;
  matched: number;
  skipped: number;
}

export interface ReconcileResultDto {
  reconciled: number;
  adjustment: TransactionDto | null;
}

// ───────── Money & net worth (loans.md §5) ─────────

export interface NetWorthRow {
  month: Month;
  /** On-budget accounts: the money the Plan distributes. */
  availableMoney: number;
  /** Off-budget accounts except loans and liabilities. */
  otherAssets: number;
  /** Loans and liabilities (≤ 0). */
  debts: number;
  /** availableMoney + otherAssets + debts. */
  total: number;
  byCurrency: Record<string, { native: number; base: number }>;
  loans: { accountId: string; name: string; currency: Currency; balance: number; balanceBase: number }[];
}

// ───────── Scheduled transactions (scheduled-transactions.md) ─────────

export type ScheduledKind = 'fixed' | 'loan_interest';

export interface ScheduledSplitDto {
  categoryId: CategoryRef;
  categoryName: string;
  amount: number;
  memo: string;
}

export interface ScheduledDto {
  id: string;
  accountId: string;
  accountName: string;
  currency: Currency;
  payeeName: string;
  categoryId: CategoryRef;
  categoryName: string;
  /** Native, signed. For `loan_interest` this is an estimate from the current balance. */
  amount: number;
  memo: string;
  flag: Flag | null;
  transfer: { accountId: string; accountName: string; currency: Currency; amount: number | null } | null;
  kind: ScheduledKind;
  frequency: Frequency;
  dateFirst: IsoDate;
  dateNext: IsoDate;
  endDate: IsoDate | null;
  splits: ScheduledSplitDto[];
  nextOccurrences: IsoDate[];
  /** The loan this schedule belongs to (installment or interest), if any. */
  loanAccountId: string | null;
}

export interface ScheduledInput {
  accountId: string;
  payeeName?: string;
  categoryId?: CategoryRef;
  amount: number;
  memo?: string;
  flag?: Flag | null;
  transferAccountId?: string | null;
  /** The other leg's fixed native amount (positive), for cross-currency transfers. */
  transferAmount?: number | null;
  frequency: Frequency;
  /** Date of the next occurrence. Changing it re-bases the series. */
  dateNext: IsoDate;
  endDate?: IsoDate | null;
  splits?: SplitInput[];
}

// ───────── Loans (loans.md) ─────────

export interface LoanDto {
  accountId: string;
  name: string;
  currency: Currency;
  /** What is still owed (positive). */
  balance: number;
  originalPrincipal: number;
  startDate: IsoDate;
  termMonths: number | null;
  installment: number;
  paymentDay: number;
  fromAccountId: string | null;
  fromAccountName: string;
  categoryId: string | null;
  categoryName: string;
  interestMethod: InterestMethod;
  interestStart: Month;
  rates: LoanRate[];
  currentRate: number;
  /** The bank's effective rate (EKS), percent × 10 000; display only. */
  effectiveRate: number | null;
  /** First payment under the contract (the payment day after `startDate`). */
  firstPayment: IsoDate;
  /** Last payment under the contract, when the term is known. */
  lastPayment: IsoDate | null;
  nextPayment: IsoDate | null;
  interestPaid: number;
  /** 0–1 share of the original principal already repaid. */
  paidOff: number;
  projection: { payoffDate: IsoDate | null; totalInterest: number; months: number };
  installmentScheduleId: string | null;
  interestScheduleId: string | null;
}

export interface LoanInput {
  name: string;
  institution?: string;
  currency: Currency;
  /** What is owed on `balanceDate` (positive). */
  balance: number;
  balanceDate: IsoDate;
  originalPrincipal?: number;
  startDate?: IsoDate;
  termMonths?: number | null;
  /** Percent × 10 000. */
  annualRate: number;
  installment: number;
  /** Date of the next installment; its day of month is the payment day. */
  firstPayment: IsoDate;
  fromAccountId: string;
  /** An existing payment category, or null to create one named after the loan. */
  categoryId?: string | null;
  interestMethod?: InterestMethod;
  effectiveRate?: number | null;
}

export interface LoanPatch {
  installment?: number;
  fromAccountId?: string;
  categoryId?: string;
  interestMethod?: InterestMethod;
  termMonths?: number | null;
  /** The amount borrowed. */
  originalPrincipal?: number;
  /** When the money was paid out; the first rate is effective from it. */
  startDate?: IsoDate;
  /** The contract's nominal rate: replaces the first rate (later rate changes are kept). */
  annualRate?: number;
  effectiveRate?: number | null;
  /** Re-bases the payment and interest schedules. */
  nextPayment?: IsoDate;
  /** Day of the month payments are due (1–31); defaults to the day of `nextPayment`. */
  paymentDay?: number;
  /** What is owed at the end of `balanceDate` (positive); an adjustment is entered on the loan to match. */
  balance?: number;
  balanceDate?: IsoDate;
}

export interface SwitchCurrencyResultDto {
  accountId: string;
  oldAccountId: string;
  transferId: string | null;
  /** Schedules whose amounts were converted at the switch rate and should be checked. */
  reviewSchedules: string[];
}

// ───────── YNAB import (ynab-import.md) ─────────

export interface YnabBudgetSummaryDto {
  id: string;
  name: string;
  currency: string | null;
  firstMonth: string | null;
  lastMonth: string | null;
  lastModified: string | null;
}

export interface YnabLoanTermsDto {
  ynabAccountId: string;
  name: string;
  balance: number;
  originalPrincipal: number;
  annualRate: number;
  installment: number;
  paymentDay: number;
  interestMethod: InterestMethod;
  firstPayment: IsoDate | null;
  fromAccountName: string | null;
  categoryName: string | null;
  createInterestSchedule: boolean;
}

export interface YnabLoanTermsInput {
  ynabAccountId: string;
  annualRate?: number;
  installment?: number;
  interestMethod?: InterestMethod;
  createInterestSchedule?: boolean;
}

export interface YnabCountsDto {
  accounts: number;
  loans: number;
  groups: number;
  categories: number;
  targets: number;
  months: number;
  assignments: number;
  payees: number;
  transactions: number;
  splits: number;
  transfers: number;
  schedules: number;
}

export interface YnabNoteDto {
  kind: string;
  message: string;
}

export interface YnabDiffDto {
  kind: 'account' | 'category' | 'rta' | 'schedule';
  month: Month | null;
  name: string;
  field: string;
  koverta: number;
  ynab: number;
}

export interface YnabVerificationDto {
  ok: boolean;
  checkedAccounts: number;
  checkedCategoryMonths: number;
  /** First diverging month per category/field, plus account, RTA and schedule mismatches. */
  diffs: YnabDiffDto[];
  ageOfMoney: { koverta: number | null; ynab: number | null };
}

export interface YnabImportPreviewDto {
  budget: { id: string; name: string; currency: string };
  counts: YnabCountsDto;
  loans: YnabLoanTermsDto[];
  warnings: YnabNoteDto[];
  skipped: YnabNoteDto[];
  removes: { categories: number; accounts: number };
  verification: YnabVerificationDto;
}

export interface YnabImportResultDto extends YnabImportPreviewDto {
  batchId: string;
}
